import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { HeaderNavItem, PageRoute } from '../../types.ts';
import { buildWaUrl } from '../../lib/contactUtils.ts';
import {
  Compass,
  Briefcase,
  ShoppingBag,
  BookOpen,
  Info,
  PhoneCall,
  MessageCircle,
  Lock,
  Menu,
  X,
  Search,
  SlidersHorizontal,
  Layers,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  FolderOpen,
  Image as ImageIcon,
  ArrowRight,
} from 'lucide-react';

export default function Navbar() {
  const {
    settings,
    currentPage,
    navigate,
    user,
    logoutUser,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    categories,
    products = [],
    currentProductSlug,
  } = useApp();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [blogDropdownOpen, setBlogDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const formatRupiah = (val: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val);
  };

  // Detect scroll position for transparent header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 25);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check whether to show the public login button
  const hasSecretConfig = Boolean(settings?.security?.customLoginPath?.trim());
  const hideLoginBtn = hasSecretConfig || settings?.security?.hidePublicLoginButtons;

  const defaultNavLinks: HeaderNavItem[] = [
    { id: 'nav-blog', label: 'Artikel & Berita', type: 'page', pageTarget: 'blog', enabled: true, order: 1 },
    { id: 'nav-products', label: 'Layanan Hukum', type: 'page', pageTarget: 'products', enabled: true, order: 2 },
    { id: 'nav-gallery', label: 'Galeri & Media', type: 'page', pageTarget: 'gallery', enabled: true, order: 3 },
    { id: 'nav-about', label: 'Tentang Kami', type: 'page', pageTarget: 'about', enabled: true, order: 4 },
    { id: 'nav-contact', label: 'Kontak & Konsultasi', type: 'page', pageTarget: 'contact', enabled: true, order: 5 },
  ];

  // Merge configured navigation or use default, ensuring 'Home' is excluded from header
  const configuredNav = settings?.headerNavigation && settings.headerNavigation.length > 0
    ? settings.headerNavigation
    : defaultNavLinks;

  const activeNavItems = configuredNav
    .filter((item) => {
      if (!item.enabled) return false;
      const target = (item.pageTarget || '').toLowerCase();
      const label = (item.label || '').toLowerCase();
      const id = (item.id || '').toLowerCase();
      // Explicitly omit Home from header nav as requested
      if (target === 'home' || label === 'home' || label === 'beranda' || id === 'nav-home') {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.order - b.order);

  const getNavIcon = (item: HeaderNavItem) => {
    if (item.type === 'category') {
      return FolderOpen;
    }
    if (item.type === 'product') {
      return ShoppingBag;
    }
    if (item.type === 'custom') {
      return ExternalLink;
    }
    switch (item.pageTarget) {
      case 'home':
        return Compass;
      case 'products':
        return ShoppingBag;
      case 'portfolio':
        return Briefcase;
      case 'blog':
        return BookOpen;
      case 'gallery':
        return ImageIcon;
      case 'media-showcase':
        return ImageIcon;
      case 'about':
        return Info;
      case 'contact':
        return PhoneCall;
      default:
        return Compass;
    }
  };

  const handleNavClick = (item: HeaderNavItem) => {
    if (item.type === 'category') {
      setSelectedCategory(item.categoryId || 'all');
      navigate('blog');
    } else if (item.type === 'product') {
      if (item.productSlug || item.productId) {
        navigate('product-detail', item.productSlug || item.productId);
      } else {
        navigate('products');
      }
    } else if (item.type === 'custom') {
      if (item.customUrl) {
        if (item.openNewTab || item.customUrl.startsWith('http')) {
          window.open(item.customUrl, '_blank', 'noopener,noreferrer');
        } else {
          const target = item.customUrl.replace(/^\//, '') as PageRoute;
          navigate(target);
        }
      }
    } else if (item.pageTarget) {
      if (item.pageTarget === 'blog') {
        setSelectedCategory('all');
      }
      navigate(item.pageTarget);
    }
    setMobileMenuOpen(false);
    setProductsDropdownOpen(false);
    setBlogDropdownOpen(false);
  };

  const isItemActive = (item: HeaderNavItem): boolean => {
    if (item.type === 'category') {
      return (
        currentPage === 'blog' &&
        (selectedCategory === item.categoryId ||
          categories.find((c) => c.id === item.categoryId)?.slug === selectedCategory)
      );
    }
    if (item.type === 'product') {
      return (
        currentPage === 'product-detail' &&
        (currentProductSlug === item.productSlug || currentProductSlug === item.productId)
      );
    }
    if (item.type === 'page') {
      return currentPage === item.pageTarget;
    }
    return false;
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate('blog');
      setSearchOpen(false);
    }
  };

  function isColorDark(color?: string): boolean {
    if (!color) return false;
    let hex = color.trim().replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('');
    }
    if (hex.length === 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness < 160;
      }
    }
    const darkKeywords = ['black', 'dark', 'navy', 'slate', 'gray', 'zinc', 'neutral', 'midnight'];
    return darkKeywords.some((k) => color.toLowerCase().includes(k));
  }

  const primaryColor = settings?.theme.primaryColor || '#059669';
  const headerBgColor = settings?.theme.headerBgColor;

  // Header Transparency on Homepage
  const transparentEnabled = settings?.theme.transparentHeaderOnHome !== false;
  const isHome = currentPage === 'home';
  const isTransparent = isHome && !isScrolled && transparentEnabled;

  // Contrast Calculation
  // When transparent over hero media, background is dark photo/video with gradient overlay
  const isHeaderDark = isTransparent || (headerBgColor ? isColorDark(headerBgColor) : false);

  return (
    <header
      id="main-site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        isTransparent
          ? 'bg-gradient-to-b from-black/85 via-black/40 to-transparent border-none -mb-16 sm:-mb-20'
          : headerBgColor
          ? 'backdrop-blur-md shadow-md border-b border-black/10'
          : isHeaderDark
          ? 'bg-slate-950/95 backdrop-blur-md shadow-md border-b border-slate-800'
          : 'bg-white/95 backdrop-blur-md shadow-xs border-b border-slate-200/80'
      }`}
      style={!isTransparent && headerBgColor ? { backgroundColor: headerBgColor } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Brand Logo with Adaptive Shadow */}
            <div
            id="brand-logo-button"
            onClick={() => navigate('home')}
            className="flex items-center cursor-pointer group shrink-0"
            title={settings?.siteName || 'Home'}
          >
            {settings?.logoUrl ? (
              <img
                src={settings.logoUrl}
                alt={settings?.siteName || 'Logo'}
                className={`h-9 sm:h-12 w-auto max-h-12 max-w-[180px] sm:max-w-[240px] object-contain transition-transform group-hover:scale-105 ${
                  isTransparent ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]' : ''
                }`}
              />
            ) : (
              <div
                className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl shadow-md transition-transform group-hover:scale-105 shrink-0 ${
                  isTransparent ? 'ring-2 ring-white/30 drop-shadow-md' : ''
                }`}
                style={{ backgroundColor: primaryColor }}
              >
                <Layers className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            )}
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-1.5 py-1">
            {activeNavItems.map((link) => {
              const isActive = isItemActive(link);
              const IconComp = getNavIcon(link);
              const isBlogLink = link.pageTarget === 'blog';
              const isProductsLink = link.pageTarget === 'products';

              // SUB-MENU FOR PRODUCTS / LAYANAN
              if (isProductsLink) {
                return (
                  <div
                    key={link.id}
                    className="relative group"
                    onMouseEnter={() => setProductsDropdownOpen(true)}
                    onMouseLeave={() => setProductsDropdownOpen(false)}
                  >
                    <button
                      id={`nav-item-${link.id}`}
                      onClick={() => handleNavClick(link)}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                        isActive
                          ? 'text-white shadow-sm'
                          : isTransparent
                          ? 'text-white/95 hover:text-white hover:bg-white/20 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]'
                          : isHeaderDark
                          ? 'text-white/90 hover:text-white hover:bg-white/15'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                      style={isActive ? { backgroundColor: primaryColor } : {}}
                      title="Explore services and solutions catalog"
                    >
                      <IconComp className={`w-3.5 h-3.5 ${isActive ? 'text-white' : isTransparent ? 'text-emerald-300 drop-shadow-sm' : isHeaderDark ? 'text-white/80' : 'text-slate-400'}`} />
                      <span>{link.label}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 group-hover:rotate-180 ${isActive ? 'text-white' : isTransparent ? 'text-white/90' : isHeaderDark ? 'text-white/80' : 'text-slate-400'}`} />
                    </button>

                    {/* Products / Services Dropdown Sub-menu */}
                    {productsDropdownOpen && (
                      <div className="absolute left-0 top-full pt-1.5 w-80 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                        <div className="p-2.5 bg-white rounded-2xl border border-slate-200 shadow-2xl space-y-1">
                          <div className="px-3 py-2 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex items-center justify-between">
                            <span className="flex items-center gap-1 text-emerald-700">
                              <ShoppingBag className="w-3 h-3" />
                              <span>Services & Solutions ({products.length})</span>
                            </span>
                            <span className="font-mono text-[9px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">CATALOG</span>
                          </div>

                          <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                            {products.slice(0, 6).map((prod) => {
                              const isCurrentProd = currentPage === 'product-detail' && currentProductSlug === prod.slug;
                              return (
                                <button
                                  key={prod.id}
                                  onClick={() => {
                                    navigate('product-detail', prod.slug || prod.id);
                                    setProductsDropdownOpen(false);
                                  }}
                                  className={`w-full text-left p-2 rounded-xl transition-all flex items-center gap-2.5 group/item ${
                                    isCurrentProd
                                      ? 'bg-emerald-50 text-emerald-900 border border-emerald-300'
                                      : 'hover:bg-slate-50 text-slate-800'
                                  }`}
                                >
                                  {prod.image ? (
                                    <img
                                      src={prod.image}
                                      alt={prod.title}
                                      className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                                      <ShoppingBag className="w-4 h-4" />
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-bold truncate group-hover/item:text-emerald-700 transition-colors">
                                      {prod.title}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5">
                                      {prod.badge && (
                                        <span className="px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 font-bold text-[9px]">
                                          {prod.badge}
                                        </span>
                                      )}
                                      {prod.hidePrice ? (
                                        prod.priceCustomLabel ? (
                                          <span className="font-semibold text-amber-700 truncate">
                                            {prod.priceCustomLabel}
                                          </span>
                                        ) : null
                                      ) : (
                                        <span className="font-semibold text-emerald-700 truncate">
                                          {typeof prod.price === 'number' ? formatRupiah(prod.price) : prod.price}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          <div className="pt-1.5 border-t border-slate-100">
                            <button
                              onClick={() => {
                                navigate('products');
                                setProductsDropdownOpen(false);
                              }}
                              className="w-full py-2 px-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                            >
                              <span>Lihat Semua Layanan Hukum</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // SUB-MENU FOR BLOG / ARTIKEL
              if (isBlogLink) {
                return (
                  <div
                    key={link.id}
                    className="relative group"
                    onMouseEnter={() => setBlogDropdownOpen(true)}
                    onMouseLeave={() => setBlogDropdownOpen(false)}
                  >
                    <button
                      id={`nav-item-${link.id}`}
                      onClick={() => handleNavClick(link)}
                      className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                        isActive
                          ? 'text-white shadow-sm'
                          : isTransparent
                          ? 'text-white/95 hover:text-white hover:bg-white/20 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]'
                          : isHeaderDark
                          ? 'text-white/90 hover:text-white hover:bg-white/15'
                          : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                      style={isActive ? { backgroundColor: primaryColor } : {}}
                      title="Explore all articles and categories"
                    >
                      <IconComp className={`w-3.5 h-3.5 ${isActive ? 'text-white' : isTransparent ? 'text-emerald-300 drop-shadow-sm' : isHeaderDark ? 'text-white/80' : 'text-slate-400'}`} />
                      <span>{link.label}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 group-hover:rotate-180 ${isActive ? 'text-white' : isTransparent ? 'text-white/90' : isHeaderDark ? 'text-white/80' : 'text-slate-400'}`} />
                    </button>

                    {/* Blog Category Dropdown */}
                    {blogDropdownOpen && (
                      <div className="absolute left-0 top-full pt-1.5 w-64 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                        <div className="p-2 bg-white rounded-2xl border border-slate-200 shadow-2xl space-y-1">
                          <div className="px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex items-center justify-between">
                            <span>Kategori Artikel ({categories.length})</span>
                          </div>

                          <button
                            onClick={() => {
                              setSelectedCategory('all');
                              navigate('blog');
                              setBlogDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-xl text-xs font-bold transition-colors flex items-center justify-between ${
                              selectedCategory === 'all'
                                ? 'bg-slate-900 text-white'
                                : 'text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <span>Semua Artikel & Berita</span>
                            <span className="text-[10px] opacity-75 font-mono">SEMUA</span>
                          </button>

                          <div className="max-h-60 overflow-y-auto space-y-0.5">
                            {categories.map((cat) => {
                              const isCatSelected = selectedCategory === cat.id || selectedCategory === cat.slug;
                              return (
                                <button
                                  key={cat.id}
                                  onClick={() => {
                                    setSelectedCategory(cat.id);
                                    navigate('blog');
                                    setBlogDropdownOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 rounded-xl text-xs font-bold transition-colors flex items-center justify-between ${
                                    isCatSelected
                                      ? 'bg-emerald-600 text-white'
                                      : 'text-slate-700 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="truncate">{cat.name}</span>
                                  {cat.count !== undefined && (
                                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${isCatSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                      {cat.count}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={link.id}
                  id={`nav-item-${link.id}`}
                  onClick={() => handleNavClick(link)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                    isActive
                      ? 'text-white shadow-sm'
                      : isTransparent
                      ? 'text-white/95 hover:text-white hover:bg-white/20 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]'
                      : isHeaderDark
                      ? 'text-white/90 hover:text-white hover:bg-white/15'
                      : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                  style={isActive ? { backgroundColor: primaryColor } : {}}
                  title={link.type === 'category' ? `Category: ${link.label}` : link.label}
                >
                  <IconComp className={`w-3.5 h-3.5 ${isActive ? 'text-white' : isTransparent ? 'text-emerald-300 drop-shadow-sm' : isHeaderDark ? 'text-white/80' : 'text-slate-400'}`} />
                  <span>{link.label}</span>
                  {link.type === 'category' && (
                    <span
                      className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase ${
                        isActive ? 'bg-white/20 text-white' : isTransparent ? 'bg-white/25 text-white' : isHeaderDark ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      Category
                    </span>
                  )}
                  {link.type === 'product' && (
                    <span
                      className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase ${
                        isActive ? 'bg-white/20 text-white' : isTransparent ? 'bg-amber-400/90 text-slate-950 font-black' : isHeaderDark ? 'bg-amber-400/90 text-slate-950' : 'bg-amber-100 text-amber-900'
                      }`}
                    >
                      Produk
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Desktop Action Tools (Search, Admin / Login button, WhatsApp CTA) */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            {/* Quick Search trigger */}
            <div className="relative">
              {searchOpen ? (
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <input
                    id="nav-search-input"
                    type="text"
                    placeholder="Search articles, services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    className="w-44 xl:w-56 pl-9 pr-3 py-1.5 text-xs bg-slate-100 border border-slate-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                  />
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5" />
                  <button
                    type="button"
                    onClick={() => setSearchOpen(false)}
                    className="ml-1 p-1 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <button
                  id="nav-search-toggle-btn"
                  onClick={() => setSearchOpen(true)}
                  className={`p-2 rounded-lg transition-colors ${
                    isTransparent
                      ? 'text-white/95 hover:text-white hover:bg-white/20 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]'
                      : isHeaderDark
                      ? 'text-white/90 hover:text-white hover:bg-white/15'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                  title="Search"
                >
                  <Search className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* If user logged in: Dashboard Button. If logged out and not hidden: Login button */}
            {user ? (
              <div className="flex items-center gap-1.5">
                <button
                  id="nav-admin-dashboard-btn"
                  onClick={() => navigate('admin')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    currentPage === 'admin'
                      ? 'bg-slate-900 text-white'
                      : isTransparent
                      ? 'bg-slate-900/90 text-amber-300 border border-amber-400/40 hover:bg-slate-900 shadow-md backdrop-blur-md'
                      : 'bg-slate-800 text-amber-300 hover:bg-slate-900 shadow-xs'
                  }`}
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  <span>Dashboard ({user.role === 'admin' ? 'Admin' : 'Editorial'})</span>
                </button>
                <button
                  id="nav-logout-btn"
                  onClick={logoutUser}
                  className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
                    isTransparent ? 'text-rose-300 hover:text-white hover:bg-rose-950/60' : 'text-rose-600 hover:text-rose-700 hover:bg-rose-50'
                  }`}
                >
                  Logout
                </button>
              </div>
            ) : (
              !hideLoginBtn && (
                <button
                  id="nav-login-btn"
                  onClick={() => navigate('login')}
                  className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-xs font-semibold ${
                    isTransparent
                      ? 'text-white/95 hover:text-white hover:bg-white/20 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]'
                      : isHeaderDark
                      ? 'text-white/90 hover:text-white hover:bg-white/15'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                  }`}
                  title="Editor Login"
                >
                  <Lock className="w-3.5 h-3.5" />
                  <span className="hidden xl:inline">Editor Login</span>
                </button>
              )
            )}


          </div>

          {/* Mobile Actions (Search, WhatsApp pill, Hamburger) */}
          <div className="flex items-center gap-1.5 sm:gap-2 lg:hidden">
            {/* Quick Mobile Search Button */}
            <button
              id="mobile-search-toggle-btn"
              onClick={() => setSearchOpen(!searchOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
                searchOpen
                  ? 'bg-slate-200 text-slate-900'
                  : isTransparent
                  ? 'text-white hover:bg-white/20 active:bg-white/30'
                  : isHeaderDark
                  ? 'text-white hover:bg-white/15 active:bg-white/25'
                  : 'text-slate-600 hover:bg-slate-100 active:bg-slate-200'
              }`}
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>



            {/* Hamburger Toggle Button */}
            <button
              id="mobile-menu-toggle-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
                mobileMenuOpen
                  ? 'bg-slate-900 text-white'
                  : isTransparent
                  ? 'bg-white/20 hover:bg-white/30 active:bg-white/40 text-white border border-white/30 backdrop-blur-md'
                  : isHeaderDark
                  ? 'bg-white/15 hover:bg-white/25 active:bg-white/30 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-800'
              }`}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar Collapsible */}
        {searchOpen && (
          <div className="py-2.5 border-t border-slate-100/30 animate-in slide-in-from-top-2 duration-150">
            <form onSubmit={handleSearchSubmit} className="relative flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  id="mobile-search-input"
                  type="text"
                  placeholder="Search articles, services, case studies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-9 pr-8 py-2 text-xs bg-white/95 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-900"
                />
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-2 p-0.5 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="px-3.5 py-2 text-xs font-bold text-white rounded-xl shadow-xs shrink-0"
                style={{ backgroundColor: primaryColor }}
              >
                Search
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div
          className={`lg:hidden border-b border-slate-200/50 px-4 pt-3 pb-6 shadow-2xl animate-in slide-in-from-top duration-200 max-h-[calc(100vh-80px)] overflow-y-auto ${
            isHeaderDark ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
          }`}
          style={!isTransparent && headerBgColor ? { backgroundColor: headerBgColor } : {}}
        >
          {/* User Status Card if Logged In */}
          {user && (
            <div className="mb-3 p-3 bg-slate-900/90 text-white border border-slate-700/60 rounded-2xl flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <img
                  src={user.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=100&q=80'}
                  alt={user.name}
                  className="w-8 h-8 rounded-full border border-slate-700 object-cover shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{user.name}</div>
                  <div className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider">
                    {user.role === 'admin' ? 'Administrator' : 'Editorial Staff'}
                  </div>
                </div>
              </div>
              <button
                id="mobile-admin-dashboard-btn"
                onClick={() => {
                  navigate('admin');
                  setMobileMenuOpen(false);
                }}
                className="px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold rounded-xl text-xs flex items-center gap-1 shadow-xs shrink-0"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span>Dashboard</span>
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <div className={`text-[10px] font-bold uppercase tracking-wider px-3 pt-1 pb-1 ${
              isHeaderDark ? 'text-white/60' : 'text-slate-400'
            }`}>
              Navigation Menu
            </div>
            {activeNavItems.map((link) => {
              const isActive = isItemActive(link);
              const IconComp = getNavIcon(link);
              const isProductsLink = link.pageTarget === 'products';
              const isBlogLink = link.pageTarget === 'blog';

              if (isProductsLink) {
                return (
                  <div key={`mobile-${link.id}`} className="space-y-1">
                    <div className="flex items-center gap-1">
                      <button
                        id={`mobile-${link.id}`}
                        onClick={() => handleNavClick(link)}
                        className={`flex-1 flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                          isActive
                            ? 'text-white shadow-xs'
                            : isHeaderDark
                            ? 'text-white/90 hover:bg-white/10 active:bg-white/20'
                            : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200'
                        }`}
                        style={isActive ? { backgroundColor: primaryColor } : {}}
                      >
                        <div className="flex items-center gap-2.5">
                          <IconComp className={`w-4 h-4 ${isActive ? 'text-white' : isHeaderDark ? 'text-white/80' : 'text-slate-500'}`} />
                          <span>{link.label}</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                        className={`p-2.5 rounded-xl transition-colors ${
                          isHeaderDark ? 'text-white/80 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        title="Open Services Sub-menu"
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {mobileProductsOpen && (
                      <div className="pl-6 pr-2 py-1 space-y-1 border-l-2 border-emerald-500/40 ml-4 animate-in slide-in-from-top-1 duration-150">
                        {products.slice(0, 6).map((p) => (
                          <button
                            key={`mobile-prod-${p.id}`}
                            onClick={() => {
                              navigate('product-detail', p.slug || p.id);
                              setMobileMenuOpen(false);
                            }}
                            className="w-full text-left py-2 px-2.5 rounded-lg text-xs font-semibold flex items-center justify-between text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                          >
                            <span className="truncate">{p.title}</span>
                            {p.hidePrice ? (
                              p.priceCustomLabel ? (
                                <span className="text-[10px] text-amber-400 font-bold shrink-0">{p.priceCustomLabel}</span>
                              ) : null
                            ) : (
                              <span className="text-[10px] text-emerald-400 font-bold shrink-0">
                                {typeof p.price === 'number' ? formatRupiah(p.price) : p.price}
                              </span>
                            )}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            navigate('products');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left py-1.5 px-2.5 text-[11px] font-bold text-amber-400 hover:underline flex items-center gap-1"
                        >
                          <span>View All Services & Solutions →</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              if (isBlogLink) {
                return (
                  <div key={`mobile-${link.id}`} className="space-y-1">
                    <div className="flex items-center gap-1">
                      <button
                        id={`mobile-${link.id}`}
                        onClick={() => handleNavClick(link)}
                        className={`flex-1 flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                          isActive
                            ? 'text-white shadow-xs'
                            : isHeaderDark
                            ? 'text-white/90 hover:bg-white/10 active:bg-white/20'
                            : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200'
                        }`}
                        style={isActive ? { backgroundColor: primaryColor } : {}}
                      >
                        <div className="flex items-center gap-2.5">
                          <IconComp className={`w-4 h-4 ${isActive ? 'text-white' : isHeaderDark ? 'text-white/80' : 'text-slate-500'}`} />
                          <span>{link.label}</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setMobileBlogOpen(!mobileBlogOpen)}
                        className={`p-2.5 rounded-xl transition-colors ${
                          isHeaderDark ? 'text-white/80 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        title="Open Categories Sub-menu"
                      >
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileBlogOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {mobileBlogOpen && (
                      <div className="pl-6 pr-2 py-1 space-y-1 border-l-2 border-emerald-500/40 ml-4 animate-in slide-in-from-top-1 duration-150">
                        <button
                          onClick={() => {
                            setSelectedCategory('all');
                            navigate('blog');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left py-1.5 px-2.5 text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/10 rounded-lg"
                        >
                          All Categories
                        </button>
                        {categories.map((c) => (
                          <button
                            key={`mobile-cat-${c.id}`}
                            onClick={() => {
                              setSelectedCategory(c.id);
                              navigate('blog');
                              setMobileMenuOpen(false);
                            }}
                            className="w-full text-left py-1.5 px-2.5 text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/10 rounded-lg flex items-center justify-between"
                          >
                            <span className="truncate">{c.name}</span>
                            <span className="text-[10px] opacity-75">{c.count || 0}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <button
                  key={`mobile-${link.id}`}
                  id={`mobile-${link.id}`}
                  onClick={() => handleNavClick(link)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all ${
                    isActive
                      ? 'text-white shadow-xs'
                      : isHeaderDark
                      ? 'text-white/90 hover:bg-white/10 active:bg-white/20'
                      : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200'
                  }`}
                  style={isActive ? { backgroundColor: primaryColor } : {}}
                >
                  <div className="flex items-center gap-2.5">
                    <IconComp className={`w-4 h-4 ${isActive ? 'text-white' : isHeaderDark ? 'text-white/80' : 'text-slate-500'}`} />
                    <span>{link.label}</span>
                    {link.type === 'category' && (
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
                        isActive ? 'bg-white/20 text-white' : isHeaderDark ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        Category
                      </span>
                    )}
                    {link.type === 'product' && (
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
                        isActive ? 'bg-white/20 text-white' : isHeaderDark ? 'bg-amber-400 text-slate-950' : 'bg-amber-100 text-amber-800'
                      }`}>
                        Service
                      </span>
                    )}
                  </div>
                  <ChevronRight className={`w-3.5 h-3.5 ${isActive ? 'text-white/80' : isHeaderDark ? 'text-white/60' : 'text-slate-400'}`} />
                </button>
              );
            })}

            <div className="border-t border-slate-200 pt-3 mt-2 flex flex-col gap-2">
              {user ? (
                <button
                  id="mobile-logout-btn"
                  onClick={() => {
                    logoutUser();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full py-2.5 text-center text-xs text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-colors"
                >
                  Sign Out (Logout)
                </button>
              ) : (
                !hideLoginBtn && (
                  <button
                    id="mobile-login-btn"
                    onClick={() => {
                      navigate('login');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors"
                  >
                    <Lock className="w-3.5 h-3.5 text-slate-600" />
                    <span>Editorial Login / Administrator</span>
                  </button>
                )
              )}

              {settings?.contact?.whatsappNumber && (
                <a
                  id="mobile-whatsapp-btn"
                  href={buildWaUrl(settings.contact.whatsappNumber, 'Halo Tim Advokat ALY LAW FIRM, saya ingin berkonsultasi mengenai permasalahan hukum.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 text-center text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-transform active:scale-[0.98]"
                  style={{ backgroundColor: primaryColor }}
                >
                  <PhoneCall className="w-4 h-4" />
                  <span>Konsultasi Hukum via WhatsApp</span>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
